"use client";

/**
 * The door, twice. TWO COMPONENTS OVER ONE CONTEXT VALUE, not one component
 * with a `collapsed` prop: the expanded surface needs a list you can act on,
 * the minimised one needs a single glanceable count that gets you back, and the
 * spike's 161 lines of `isMinimized` branching is what conflating them costs.
 *
 * Admitting happens HERE, from inside the call surface, because that is where
 * somebody is when the knock arrives. It is not a schedule action.
 */
import { Button } from "@repo/ui/components/ui/button";
import { cn } from "@repo/ui/lib/utils";
import { UserCheck } from "lucide-react";
import { createPortal } from "react-dom";
import { useCall } from "./context";

function initials(name: string): string {
  return (
    name
      .split(/[\s.]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

/**
 * The expanded surface's list: who is at the door, and the two answers.
 *
 * A COLUMN, NOT AN OVERLAY. The video sits beside it rather than under it —
 * `geometry.stageStyle` insets the stage by exactly this width — so nothing is
 * hidden behind the panel and the clinician is not choosing between seeing the
 * patient they are with and seeing the one at the door. It slides shut for the
 * consult that wants the whole frame, and the handle stays put so getting it
 * back is one click in a place that does not move.
 */
export function CallWaitingList() {
  const { waiting, admitting, geometry, actions } = useCall();

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      data-testid="call-waiting-column"
      className={cn(
        // ABOVE the chrome, not level with it. The handle sits on the seam
        // between the two layers, and at equal z-index which one receives the
        // click depends on portal order — that is, on which component happened
        // to mount first.
        "fixed z-50 transition-[left,width] duration-200 ease-out motion-reduce:transition-none",
      )}
      style={geometry.asideStyle}
    >
      <aside
        data-testid="call-waiting-aside"
        aria-hidden={!geometry.asideOpen}
        className={cn(
          "absolute inset-y-0 right-0 w-80 overflow-y-auto border-l bg-background px-4 pb-4 pt-5",
          "transition-transform duration-200 ease-out motion-reduce:transition-none",
          geometry.asideOpen ? "translate-x-0" : "pointer-events-none translate-x-full",
        )}
      >
        <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Waiting room</h2>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Nobody enters until you let them in.</p>

        {waiting.length === 0 && (
          <div className="mt-6 rounded-2xl border bg-muted/40 p-5 text-center">
            <span className="mx-auto grid size-10 place-items-center rounded-full bg-muted text-muted-foreground">
              <UserCheck className="size-5" />
            </span>
            <p className="mt-3 text-sm text-muted-foreground">Nobody is waiting</p>
          </div>
        )}

        <div className="mt-4 space-y-2.5">
          {waiting.map((entry) => (
            <div key={entry.id} className="rounded-2xl border bg-muted/40 p-3.5">
              <div className="flex items-center gap-2.5">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                  {initials(entry.displayName)}
                </span>
                <span className="min-w-0 flex-1 truncate text-sm font-medium">{entry.displayName}</span>
                <Elapsed since={entry.since} />
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  size="sm"
                  disabled={admitting === entry.id}
                  onClick={() => actions.admit(entry.id)}
                  className="flex-1 rounded-full"
                >
                  {admitting === entry.id ? "Letting in…" : "Let in"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => actions.decline(entry.id)}
                  className="flex-1 rounded-full text-muted-foreground hover:text-destructive"
                >
                  Decline
                </Button>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

/**
 * The minimised surface's chip. Minimised must never mean missing somebody at
 * the door, so it expands on click rather than acting in place — a one-tap
 * admission from a 320px card is how the wrong person gets let in.
 */
export function CallWaitingChip() {
  const { waiting, actions } = useCall();
  if (waiting.length === 0) return null;

  return (
    <Button
      size="xs"
      onClick={actions.expand}
      className="absolute left-3 top-12 rounded-full border-transparent bg-amber-400 font-bold text-black shadow hover:bg-amber-300"
    >
      {waiting.length} waiting
    </Button>
  );
}

function Elapsed({ since }: { since: number }) {
  const seconds = Math.max(0, Math.round((Date.now() - since) / 1000));
  return (
    <span className={cn("shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground")}>
      {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}
    </span>
  );
}
